// A cup (contracts/tournaments.md §6, §9; GET /admin/v1/cups/{id} and /entries): its numbers (refreshed every 30 s
// while it's open), the entries with rank, score, time, country, sign-in, flags and the entrants sharing a device,
// filtered by bracket, country or flagged; editors disqualify or reinstate an entry with a reason until the results
// are confirmed; an admin confirms the results after reviewing the flagged or unlinked entries in the money prizes'
// ranks, which makes gift-card prizes claimable. Each entry links the set it was dealt (its puzzles, answers and
// who else holds it). A live final shows each bracket's drop, qualifiers, the realtime state, lobby and results.
// Editors hide the cup from players, or show it again, at any time ("Hidden from players", PATCH { hidden }).

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { type AdminCup, type CupEntry, useConfirmCup, useCup, useCupEntries, useDisqualify, useLiveFinal, useUpdateCup } from "../../api/cups";
import { useAuth, useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Select, Stat } from "../../components/ui";
import { AWARD_STATUSES, AWARD_TONE, flag, GIFT_CARD_LABEL, type GiftCard, KIND_LABEL, MONEY_KINDS, PRIZE_KIND_LABEL, prizeValueText, setNumber, STATUS_TONE } from "../../lib/cups";
import { formatDateTime } from "../../lib/dates";
import { fmtInt, fmtMs, fmtPct, shortId } from "../../lib/format";
import { SwitchRow } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import l from "../learn/learn.module.css";
import { CheckRow, cupTitle, cupWhen, hasMoneyPrizes } from "./components";
import c from "./cups.module.css";

const FLAG_LABEL: Record<string, string> = { too_fast: "too fast", impossible_timing: "impossible timing" };
const who = (e: { handle: string | null; userId: string }) => (e.handle ? `@${e.handle}` : shortId(e.userId));

/** The same prize in several brackets on one line: “$50 gift card · #1 · rookie, challenger, master”. */
function groupPrizes(cup: AdminCup) {
  const groups = new Map<string, { key: string; label: string; ranks: string; kind: AdminCup["prizes"][number]["kind"]; country: string | null; minEntrants: number; giftCards: string[]; brackets: string[] }>();
  for (const p of cup.prizes) {
    const label = p.content.en?.label || prizeValueText(p);
    const ranks = p.rankTo > p.rankFrom ? `${p.rankFrom}–${p.rankTo}` : String(p.rankFrom);
    const key = [p.kind, p.value, label, ranks, p.country ?? "", p.minEntrants, p.giftCards.join()].join("|");
    const g = groups.get(key) ?? { key, label, ranks, kind: p.kind, country: p.country, minEntrants: p.minEntrants, giftCards: p.giftCards, brackets: [] };
    g.brackets.push(p.bracket);
    groups.set(key, g);
  }
  return [...groups.values()];
}

export function CupDetailPage() {
  const { id = "" } = useParams();
  const cup = useCup(id, true);
  if (cup.isPending) {
    return (
      <Card>
        <Loading rows={8} />
      </Card>
    );
  }
  if (cup.isError) {
    return (
      <Card>
        <EmptyState title="No such cup" action={<Link to="/tournaments">← Tournaments</Link>}>
          <ErrorState error={cup.error} onRetry={() => void cup.refetch()} />
        </EmptyState>
      </Card>
    );
  }
  return <CupDetail cup={cup.data} />;
}

function CupDetail({ cup }: { cup: AdminCup }) {
  const canEdit = useCan("cups.edit");
  const canConfirm = useCan("cups.confirm");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const all = useCupEntries(cup.id, {}, cup.status === "open");
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const toConfirm = cup.status === "finalized" && !cup.confirmedAt && hasMoneyPrizes(cup.prizes);
  const entries = all.data?.items ?? [];
  const finished = entries.filter((e) => e.finishedAt).length;
  const pending = cup.awards.pending ?? 0;
  const frozen = cup.status === "finalized" || cup.status === "cancelled";

  return (
    <>
      <PageHeader
        title={cupTitle(cup)}
        meta={
          <span className={l.statusLine}>
            <Link to="/tournaments" className={l.back}>
              ← Tournaments
            </Link>
            <Chip tone={STATUS_TONE[cup.status]}>{cup.status}</Chip>
            {cup.hidden ? <Chip tone="violet">Hidden</Chip> : null}
            {toConfirm ? <Chip tone="warning">results to confirm</Chip> : null}
            {cup.confirmedAt ? <span data-testid="confirmed">results confirmed {formatDateTime(cup.confirmedAt)}</span> : null}
            <span>
              {KIND_LABEL[cup.kind]}
              {cup.number && !cupTitle(cup).includes(String(cup.number)) ? ` #${cup.number}` : ""} · {cupWhen(cup)}
            </span>
            {!canEdit ? (
              <span className={styles.readOnly} data-testid="read-only">
                Read-only · managing cups needs the editor role (you are {admin?.role})
              </span>
            ) : null}
          </span>
        }
        actions={
          <div className={l.headActions}>
            {canEdit && !frozen ? (
              <Button variant="secondary" onClick={() => navigate(`/tournaments/${cup.id}/edit`)} data-testid="edit-cup">
                {cup.status === "draft" || cup.status === "scheduled" ? "Edit" : "Edit text, prizes & close"}
              </Button>
            ) : null}
            {toConfirm && canConfirm ? (
              <Button variant="primary" onClick={() => setConfirming(true)} data-testid="confirm-results">
                Confirm results…
              </Button>
            ) : null}
          </div>
        }
      />
      {notice ? (
        <p className={l.savedNotice} data-testid="cup-notice">
          {notice}
        </p>
      ) : null}
      {toConfirm && !canConfirm ? (
        <div className={m.notice} data-tone="info" style={{ marginBottom: 16 }} data-testid="confirm-needs-admin">
          <strong>The results wait for an admin's confirmation.</strong>
          <span>Gift-card winners can claim once an admin reviews the flagged top entries and confirms (you are {admin?.role}).</span>
        </div>
      ) : null}
      {cup.hidden && cup.status !== "cancelled" ? (
        <div className={m.notice} data-tone="warning" style={{ marginBottom: 16 }} data-testid="hidden-note">
          <strong>Hidden from players.</strong>
          <span>The app doesn't list it, nobody new can enter and it sends no pushes. Its entrants still reach it and its winners can claim; the cups job runs it as usual.</span>
        </div>
      ) : null}
      {cup.status === "draft" ? (
        <div className={m.notice} data-tone="info" style={{ marginBottom: 16 }} data-testid="draft-note">
          <strong>A draft: players don't see it.</strong>
          <span>Publish it from the editor when it's ready.</span>
        </div>
      ) : null}

      <div className={styles.statStrip} data-testid="cup-stats">
        <Stat label="Entrants" value={fmtInt(cup.entrants)} sub={cup.status === "open" ? "live · every 30 s" : undefined} />
        <Stat label="Finished" value={all.data ? fmtInt(finished) : "—"} />
        <Stat label="Completion" value={all.data && cup.entrants ? fmtPct(finished / cup.entrants) : "—"} />
        <Stat label="Flagged" value={all.data ? fmtInt(entries.filter((e) => e.flags.length || e.duplicates.length).length) : "—"} sub="fast play or a shared device" />
        <Stat label="Disqualified" value={all.data ? fmtInt(entries.filter((e) => e.status === "disqualified").length) : "—"} />
      </div>

      <div className={c.detailGrid}>
        <div className={c.stack}>
          <Entries cup={cup} canEdit={canEdit} all={entries} />
        </div>
        <aside className={c.side}>
          <Card title="Visibility">
            <Visibility cup={cup} canEdit={canEdit} />
          </Card>
          {cup.final?.mode === "live" ? <LiveFinalCard cup={cup} /> : null}
          <Card title="Brackets">
            <ul className={c.bracketBars} data-testid="bracket-bars">
              {cup.brackets.map((b) => {
                const mine = entries.filter((e) => e.bracket === b.id);
                const done = mine.filter((e) => e.finishedAt).length;
                return (
                  <li key={b.id}>
                    <span>
                      <strong>{b.name || b.id}</strong>
                      <span className={c.when}>
                        lv {b.minLevel}
                        {b.maxLevel == null ? "+" : `–${b.maxLevel}`}
                        {cup.qualifiers[b.id] !== undefined ? ` · ${cup.qualifiers[b.id]} qualified` : ""}
                      </span>
                    </span>
                    <span className={c.meter} title={`${done} of ${mine.length} finished`}>
                      <span style={{ width: `${mine.length ? Math.round((done / mine.length) * 100) : 0}%` }} />
                    </span>
                    <span className={c.mono}>
                      {fmtInt(done)}/{fmtInt(mine.length)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
          <Card title="Cup">
            <dl className={c.facts}>
              <dt>Opens</dt>
              <dd>{formatDateTime(cup.opensAt)}</dd>
              <dt>Closes</dt>
              <dd>{formatDateTime(cup.closesAt)}</dd>
              <dt>Week</dt>
              <dd>
                {cup.week} · <Link to={`/tournaments/puzzles?week=${cup.week}`}>its puzzle pools</Link>
              </dd>
              <dt>Set</dt>
              <dd>{cup.setSize} puzzles a player</dd>
              {cup.qualifying ? (
                <>
                  <dt>Qualifying</dt>
                  <dd>
                    {cup.qualifying.month} · best {cup.qualifying.bestOf} · top {cup.qualifying.perBracket} a bracket
                    {cup.qualifiedAt ? ` · frozen ${formatDateTime(cup.qualifiedAt)}` : ""}
                  </dd>
                </>
              ) : null}
              {cup.final ? (
                <>
                  <dt>Final</dt>
                  <dd>{cup.final.mode === "live" && cup.final.liveAt ? `live at ${formatDateTime(cup.final.liveAt)}` : "async (play any time)"}</dd>
                </>
              ) : null}
              {cup.finalizedAt ? (
                <>
                  <dt>Finalized</dt>
                  <dd>{formatDateTime(cup.finalizedAt)}</dd>
                </>
              ) : null}
              <dt>Languages</dt>
              <dd>{Object.keys(cup.content).length}</dd>
              <dt>Created</dt>
              <dd>{formatDateTime(cup.createdAt)}</dd>
            </dl>
          </Card>
          <Card title={`Prizes · ${cup.prizes.length}`}>
            {cup.prizes.length ? (
              <ul className={c.prizeList} data-testid="prize-list">
                {groupPrizes(cup).map((g) => (
                  <li key={g.key}>
                    <span>{g.label}</span>
                    <span className={c.dim}>
                      #{g.ranks} · {g.brackets.join(", ")}
                      {g.country ? ` · ${flag(g.country)} ${g.country}` : ""} · min {g.minEntrants}
                      {g.giftCards.length ? ` · ${g.giftCards.map((x) => GIFT_CARD_LABEL[x as GiftCard] ?? x).join(" or ")}` : ""}
                    </span>
                    <Chip tone={MONEY_KINDS.has(g.kind) ? "warning" : "muted"}>{PRIZE_KIND_LABEL[g.kind]}</Chip>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.faint}>No prizes: XP and badges for finishing still apply.</p>
            )}
            {Object.keys(cup.awards).length ? (
              <p className={c.awardCounts} data-testid="award-counts">
                {AWARD_STATUSES.filter((s) => cup.awards[s]).map((s) => (
                  <Chip key={s} tone={AWARD_TONE[s]}>
                    {cup.awards[s]} {s}
                  </Chip>
                ))}
              </p>
            ) : null}
            {hasMoneyPrizes(cup.prizes) ? (
              <p className={styles.footNote}>
                <Link to="/tournaments/prizes">Prize queue →</Link>
              </p>
            ) : null}
          </Card>
        </aside>
      </div>

      {confirming ? (
        <ConfirmResults
          cup={cup}
          entries={entries}
          loading={all.isPending}
          pending={pending}
          onClose={() => setConfirming(false)}
          onDone={() => {
            setConfirming(false);
            setNotice(`Results confirmed: ${pending} ${pending === 1 ? "winner" : "winners"} can now claim their prize.`);
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- visibility

/** "Hidden from players": saved at once (PATCH { hidden }), any time, a finalized or cancelled cup's too. */
function Visibility({ cup, canEdit }: { cup: AdminCup; canEdit: boolean }) {
  const update = useUpdateCup();
  const checked = update.isPending && update.variables ? Boolean(update.variables.patch.hidden) : cup.hidden;
  return (
    <div className={c.cardBody}>
      <CheckRow
        title="Hidden from players"
        hint={
          cup.hidden
            ? "Not listed in the app, no new entries, no pushes; its entrants still reach it. Untick to show it again."
            : "Tick to take it out of the app at once: no listing, no new entries, no pushes. Its entrants keep it."
        }
        checked={checked}
        onChange={(hidden) => update.mutate({ id: cup.id, patch: { hidden } })}
        disabled={!canEdit || update.isPending}
        testId="cup-hidden"
      />
      <InlineError error={update.error} />
    </div>
  );
}

// ---------------------------------------------------------------- entries

function Entries({ cup, canEdit, all }: { cup: AdminCup; canEdit: boolean; all: CupEntry[] }) {
  const [bracket, setBracket] = useState("");
  const [country, setCountry] = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const entries = useCupEntries(cup.id, { bracket: bracket || undefined, country: country || undefined, flagged: onlyFlagged || undefined }, cup.status === "open");
  const [acting, setActing] = useState<CupEntry | null>(null);
  const countries = [...new Set(all.map((e) => e.country).filter((x): x is string => Boolean(x)))].sort();
  const handles = new Map(all.map((e) => [e.userId, e.handle]));
  const items = entries.data?.items ?? [];
  // The API refuses disqualifying once the results are confirmed (void the award instead).
  const canAct = canEdit && cup.status !== "cancelled" && !cup.confirmedAt;

  return (
    <Card
      title={entries.data ? `Entries · ${fmtInt(items.length)}` : "Entries"}
      padded={false}
      actions={
        <div className={c.filters} style={{ margin: 0 }}>
          <Select value={bracket} onChange={(e) => setBracket(e.target.value)} aria-label="Bracket" data-testid="entries-bracket" style={{ width: 150 }}>
            <option value="">Every bracket</option>
            {cup.brackets.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || b.id}
              </option>
            ))}
          </Select>
          <Select value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country" data-testid="entries-country" style={{ width: 150 }}>
            <option value="">Every country</option>
            {countries.map((x) => (
              <option key={x} value={x}>
                {flag(x)} {x}
              </option>
            ))}
          </Select>
          <SwitchRow title="Flagged only" checked={onlyFlagged} onChange={setOnlyFlagged} label="Flagged only" testId="entries-flagged" />
        </div>
      }
    >
      {cup.confirmedAt && canEdit ? (
        <p className={styles.faint} style={{ padding: "0 20px 10px" }} data-testid="dq-locked">
          Results are confirmed: entries can't be disqualified any more. Void an award in the prize queue instead (its prize passes down).
        </p>
      ) : null}
      {entries.isPending ? (
        <Loading rows={6} />
      ) : entries.isError ? (
        <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={cup.status === "scheduled" || cup.status === "draft" ? "Not open yet" : "No entries"}>
          {cup.status === "scheduled" || cup.status === "draft" ? `Entries arrive when it opens, ${formatDateTime(cup.opensAt)}.` : bracket || country || onlyFlagged ? "Nothing matches these filters." : "Nobody entered."}
        </EmptyState>
      ) : (
        <div className={tableStyles.wrap} style={{ border: 0, borderRadius: 0 }}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th data-align="right">Rank</th>
                <th>Player</th>
                <th data-align="right">Score</th>
                <th data-align="right">Time</th>
                <th className={c.wideOnly} title="The set of puzzles this entrant was dealt">
                  Set
                </th>
                <th>Flags</th>
                {canAct ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.userId} className={e.status === "disqualified" ? c.dqRow : undefined} data-testid="entry-row" data-status={e.status}>
                  <td data-align="right" className={tableStyles.mono}>
                    {e.rank ?? "—"}
                    {e.countryRank && e.country ? (
                      <span className={c.when}>
                        {flag(e.country)} #{e.countryRank}
                      </span>
                    ) : null}
                    {e.livePlacement !== null ? <span className={c.when}>final #{e.livePlacement}</span> : null}
                  </td>
                  <td>
                    <Link to={`/players/${e.userId}`} className={c.player}>
                      {who(e)}
                    </Link>{" "}
                    <span title={e.country ?? "No known country: global board only"}>{e.country ? flag(e.country) : "🌐"}</span>
                    <span className={c.when} title={e.finishedAt ? `Finished ${formatDateTime(e.finishedAt)}` : `${e.played} of ${cup.setSize} played`}>
                      {e.bracket}
                      {e.playUp ? <span title="Picked a higher bracket than their level"> · played up</span> : null} ·{" "}
                      {e.linked ? "signed in" : <span className={m.required}>no sign-in</span>}
                      {e.finishedAt ? null : ` · ${e.played}/${cup.setSize} played`}
                    </span>
                    {e.setId ? (
                      <span className={c.setLine}>
                        set <SetLink setId={e.setId} />
                      </span>
                    ) : null}
                    {e.status === "disqualified" && e.dqReason ? <span className={c.reason}>Disqualified: {e.dqReason}</span> : null}
                  </td>
                  <td data-align="right" className={tableStyles.mono} title={`${fmtInt(e.points)} points`}>
                    {fmtInt(e.score)}
                  </td>
                  <td data-align="right" className={tableStyles.mono}>
                    {fmtMs(e.timeMs)}
                  </td>
                  <td className={c.wideOnly} data-testid="entry-set">
                    {e.setId ? <SetLink setId={e.setId} /> : <span className={c.dim} title="No set dealt">—</span>}
                  </td>
                  <td>
                    <div className={c.flags}>
                      {e.flags.map((f) => (
                        <Chip key={f} tone="warning">
                          {FLAG_LABEL[f] ?? f}
                        </Chip>
                      ))}
                      {e.duplicates.map((d) => (
                        <span key={d} className={c.dupe} data-testid="duplicate">
                          same device as <Link to={`/players/${d}`}>{who({ userId: d, handle: handles.get(d) ?? null })}</Link>
                        </span>
                      ))}
                      {!e.flags.length && !e.duplicates.length ? <span className={c.dim}>—</span> : null}
                    </div>
                  </td>
                  {canAct ? (
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => setActing(e)} data-testid={e.status === "active" ? "disqualify" : "reinstate"}>
                        {e.status === "active" ? "Disqualify…" : "Reinstate…"}
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {acting ? <DisqualifyDialog cup={cup} entry={acting} onClose={() => setActing(null)} /> : null}
    </Card>
  );
}

/** The entrant's set (its puzzles with answers, and who else holds it): a column when wide, the player's line when not. */
function SetLink({ setId }: { setId: string }) {
  return (
    <Link to={`/tournaments/puzzles/${encodeURIComponent(setId)}`} className={c.setLink} title={`${setId}: its puzzles with answers, and who else got it`}>
      {setNumber(setId)}
    </Link>
  );
}

const REASON = { min: 3, max: 200 };

function DisqualifyDialog({ cup, entry, onClose }: { cup: AdminCup; entry: CupEntry; onClose: () => void }) {
  const dq = useDisqualify();
  const [reason, setReason] = useState(entry.flags.includes("too_fast") ? "Answers came in faster than a person can read them." : entry.duplicates.length ? "More than one account played from the same device." : "");
  const reinstate = entry.status === "disqualified";
  const trimmed = reason.trim();
  if (reinstate) {
    return (
      <ConfirmDialog
        open
        title={`Reinstate ${who(entry)}?`}
        confirmLabel="Reinstate"
        busy={dq.isPending}
        error={dq.error}
        onCancel={onClose}
        onConfirm={() => dq.mutate({ id: cup.id, userId: entry.userId, reason: null }, { onSuccess: onClose })}
      >
        <p className={styles.prose}>Their entry goes back on the boards{cup.status === "finalized" ? " and the prizes are planned again" : ""}.</p>
        {entry.dqReason ? <p className={styles.footNote}>Disqualified for: {entry.dqReason}</p> : null}
      </ConfirmDialog>
    );
  }
  return (
    <Dialog
      open
      title={`Disqualify ${who(entry)}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={dq.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={dq.isPending}
            disabled={trimmed.length < REASON.min || trimmed.length > REASON.max}
            onClick={() => dq.mutate({ id: cup.id, userId: entry.userId, reason: trimmed }, { onSuccess: onClose })}
            data-testid="dq-confirm"
          >
            Disqualify
          </Button>
        </>
      }
    >
      <p className={styles.prose}>
        Their entry leaves the boards (rank {entry.rank ?? "—"} in {entry.bracket}); everyone below moves up.{cup.status === "finalized" ? " The prizes are planned again: theirs passes down." : ""}
      </p>
      <Field label="Reason" hint={`${trimmed.length}/${REASON.max} · at least ${REASON.min} characters`}>
        <textarea className={`${styles.textarea} ${c.textarea}`} value={reason} maxLength={REASON.max} onChange={(e) => setReason(e.target.value)} data-testid="dq-reason" />
      </Field>
      <InlineError error={dq.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------- confirm results

/** The flagged, device-sharing or unlinked entries inside the money prizes' ranks, each to tick as reviewed. */
function ConfirmResults({ cup, entries, loading, pending, onClose, onDone }: { cup: AdminCup; entries: CupEntry[]; loading: boolean; pending: number; onClose: () => void; onDone: () => void }) {
  const confirm = useConfirmCup();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const top = new Map<string, number>();
  for (const p of cup.prizes) if (MONEY_KINDS.has(p.kind) && !p.country) top.set(p.bracket, Math.max(top.get(p.bracket) ?? 0, p.rankTo));
  const review = entries.filter((e) => e.status === "active" && e.rank !== null && e.rank <= (top.get(e.bracket) ?? 0) && (e.flags.length > 0 || e.duplicates.length > 0 || !e.linked));
  const ready = !loading && review.every((e) => checked.has(e.userId));
  const handles = new Map(entries.map((e) => [e.userId, e.handle]));

  return (
    <ConfirmDialog
      open
      title={`Confirm the results of “${cupTitle(cup)}”?`}
      confirmLabel="Confirm results"
      busy={confirm.isPending}
      error={confirm.error}
      confirmDisabled={!ready}
      onCancel={onClose}
      onConfirm={() => confirm.mutate(cup.id, { onSuccess: onDone })}
    >
      <p className={styles.prose}>
        {fmtInt(pending)} pending {pending === 1 ? "prize becomes" : "prizes become"} claimable and the winners get a push. Disqualify anyone who shouldn't win first: their prize passes down. Unlinked
        players never win Pro days or money prizes (theirs pass down already).
      </p>
      {loading ? (
        <Loading rows={3} />
      ) : review.length === 0 ? (
        <p className={l.ready} data-testid="review-clear">
          No flagged entries in the prize ranks.
        </p>
      ) : (
        <>
          <span className="monoLabel">Review {review.length} top {review.length === 1 ? "entry" : "entries"}</span>
          <ul className={c.review} data-testid="review-list">
            {review.map((e) => (
              <li key={e.userId}>
                <input
                  type="checkbox"
                  checked={checked.has(e.userId)}
                  aria-label={`Reviewed ${who(e)}`}
                  onChange={(ev) =>
                    setChecked((s) => {
                      const next = new Set(s);
                      if (ev.target.checked) next.add(e.userId);
                      else next.delete(e.userId);
                      return next;
                    })
                  }
                />
                <div>
                  <strong>
                    #{e.rank} {e.bracket} · {who(e)} {e.country ? flag(e.country) : ""}
                  </strong>
                  <span>
                    {[
                      ...e.flags.map((f) => FLAG_LABEL[f] ?? f),
                      ...e.duplicates.map((d) => `same device as ${who({ userId: d, handle: handles.get(d) ?? null })}`),
                      ...(e.linked ? [] : ["no Apple or Google sign-in"]),
                    ].join(" · ")}
                  </span>
                  <Link to={`/players/${e.userId}`} target="_blank" rel="noreferrer" className={c.dupe}>
                    Open player ↗
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------- live final

/** The realtime service's bracket states. */
const LIVE_TONE: Record<string, string> = { scheduled: "info", lobby: "accent", running: "accent", finished: "success", not_enough_players: "warning", interrupted: "danger" };

function LiveFinalCard({ cup }: { cup: AdminCup }) {
  const live = useLiveFinal(cup.id, true);
  return (
    <Card title="Live final" actions={<span className={c.dim}>every 15 s</span>}>
      {live.isPending ? (
        <Loading rows={3} />
      ) : live.isError ? (
        <ErrorState error={live.error} onRetry={() => void live.refetch()} />
      ) : !live.data.live ? (
        <p className={styles.faint}>No live final.</p>
      ) : (
        <>
          <p className={styles.faint} data-testid="live-registered">
            {live.data.liveAt ? `T0 ${formatDateTime(live.data.liveAt)} · ` : ""}
            {live.data.registeredAt ? `registered with the realtime service ${formatDateTime(live.data.registeredAt)}` : "not registered with the realtime service yet"}
          </p>
          <ul className={c.liveList} data-testid="live-final">
            {(live.data.brackets ?? []).map((b) => (
              <li key={b.id}>
                <strong>{b.id}</strong>
                <Chip tone={b.state ? (LIVE_TONE[b.state] ?? "info") : b.resultsAt ? "success" : live.data.registeredAt ? "info" : "muted"}>
                  {b.state ? b.state.replace(/_/g, " ") : b.resultsAt ? "results in" : live.data.registeredAt ? "registered" : "pending"}
                </Chip>
                <span>
                  {fmtInt(b.qualifiers)} qualifiers
                  {b.lobby != null ? ` · ${fmtInt(b.lobby)} in the lobby` : ""}
                  {b.resultsAt ? ` · results ${formatDateTime(b.resultsAt)}` : ""} · <span className={c.mono}>{b.dropId}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
